function readFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readArray(value) {
  return Array.isArray(value) ? value : [];
}

function pickTraceTimes(processed, sampleCount) {
  const candidates = [
    processed?.resampledTimesSeconds,
    processed?.timesSeconds,
    processed?.timeSeconds,
    processed?.time_seconds,
  ];

  return (
    candidates.find(
      (candidate) => Array.isArray(candidate) && candidate.length === sampleCount,
    ) || []
  );
}

function toTimeValuePoints(values, times, samplingRateHz = 30) {
  if (!Array.isArray(values)) return [];

  return values
    .map((value, index) => {
      if (typeof value !== "number" || !Number.isFinite(value)) return null;

      const t =
        Array.isArray(times) &&
        typeof times[index] === "number" &&
        Number.isFinite(times[index])
          ? times[index]
          : index / samplingRateHz;

      return { t, value, index };
    })
    .filter(Boolean);
}

export function pickPostExertionRrAvg(docData) {
  return readFiniteNumber(
    docData?.results?.mpm?.rr_avg ??
      docData?.results?.mpm?.respiratory_rate?.rr_avg ??
      docData?.results?.mpm?.respiratory_rate?.metrics?.rr_avg,
  );
}

export function pickPostExertionRrMin(docData) {
  return readFiniteNumber(
    docData?.results?.mpm?.rr_min ??
      docData?.results?.mpm?.respiratory_rate?.minBreathsPerMinute ??
      docData?.results?.mpm?.respiratory_rate?.metrics?.minBreathsPerMinute,
  );
}

export function pickPostExertionRrMax(docData) {
  return readFiniteNumber(
    docData?.results?.mpm?.rr_max ??
      docData?.results?.mpm?.respiratory_rate?.maxBreathsPerMinute ??
      docData?.results?.mpm?.respiratory_rate?.metrics?.maxBreathsPerMinute,
  );
}

export function pickPostExertionBreathingRecovery(docData) {
  return docData?.results?.mpm?.breathing_recovery ?? null;
}

export function pickPostExertionRecoveryMetrics(docData) {
  const mpm = docData?.results?.mpm ?? null;
  const recovery = pickPostExertionBreathingRecovery(docData);

  return {
    rrRecovery60: readFiniteNumber(
      mpm?.rr_recovery_60s_bpm ??
        recovery?.rrAt60SecondsExpFit ??
        recovery?.rrAt60SecondsObserved ??
        recovery?.rrAt60SecondsLinearFit,
    ),
    rrRecovery120: readFiniteNumber(
      mpm?.rr_recovery_120s_bpm ??
        recovery?.rrAt120SecondsExpFit ??
        recovery?.rrAt120SecondsObserved ??
        recovery?.rrAt120SecondsLinearFit,
    ),
    recoveryFraction60: readFiniteNumber(
      mpm?.rr_recovery_60s_fraction ??
        recovery?.recoveryFractionAt60SecondsExpFit ??
        recovery?.recoveryFractionAt60SecondsObserved,
    ),
    recoveryFraction120: readFiniteNumber(
      mpm?.rr_recovery_120s_fraction ??
        recovery?.recoveryFractionAt120SecondsExpFit ??
        recovery?.recoveryFractionAt120SecondsObserved,
    ),
    timeToRecovery90: readFiniteNumber(
      mpm?.rr_time_to_recovery_90_pct_seconds ??
        recovery?.timeToRecovery90PercentSecondsExpFit ??
        recovery?.timeToRecovery90PercentSecondsObserved,
    ),
    timeToRecovery95: readFiniteNumber(
      mpm?.rr_time_to_recovery_95_pct_seconds ??
        recovery?.timeToRecovery95PercentSecondsExpFit ??
        recovery?.timeToRecovery95PercentSecondsObserved,
    ),
  };
}

export function pickPostExertionSignal(docData) {
  const processed =
    docData?.["mpm-signal-processed"] ||
    docData?.mpmSignalProcessed ||
    docData?.mpm_signal_processed ||
    null;

  if (!processed) {
    return {
      points: [],
      detrendedPoints: [],
      smoothedPoints: [],
      maxSecs: 60,
    };
  }

  const detrendedValues = readArray(
    processed.detrendedTrace ??
      processed.detrendedSignal ??
      processed.detrended_signal ??
      processed.detrended,
  );

  const smoothedValues = readArray(
    processed.smoothedTrace ??
      processed.smoothedSignal ??
      processed.smoothed_signal ??
      processed.resampledTrace ??
      processed.trace ??
      processed.signalTrace ??
      processed.signal_trace,
  );

  const samplingRateHz =
    readFiniteNumber(processed.samplingRateHz) ??
    readFiniteNumber(processed.sampling_rate_hz) ??
    30;

  const detrendedTimes = pickTraceTimes(processed, detrendedValues.length);
  const smoothedTimes = pickTraceTimes(processed, smoothedValues.length);

  const detrendedPoints = toTimeValuePoints(
    detrendedValues,
    detrendedTimes,
    samplingRateHz,
  );
  const smoothedPoints = toTimeValuePoints(
    smoothedValues,
    smoothedTimes,
    samplingRateHz,
  );
  const referencePoints = smoothedPoints.length ? smoothedPoints : detrendedPoints;

  if (!referencePoints.length) {
    return {
      points: [],
      detrendedPoints: [],
      smoothedPoints: [],
      maxSecs: 60,
    };
  }

  const firstT = referencePoints[0]?.t ?? 0;
  const normalisedDetrendedPoints = detrendedPoints.map((point, index) => ({
    ...point,
    t: point.t - firstT,
    index,
  }));
  const normalisedSmoothedPoints = smoothedPoints.map((point, index) => ({
    ...point,
    t: point.t - firstT,
    index,
  }));

  const normalisedPoints = normalisedSmoothedPoints.length
    ? normalisedSmoothedPoints
    : normalisedDetrendedPoints;

  const lastT = Math.max(
    normalisedDetrendedPoints[normalisedDetrendedPoints.length - 1]?.t ?? 0,
    normalisedSmoothedPoints[normalisedSmoothedPoints.length - 1]?.t ?? 0,
  );

  return {
    points: normalisedPoints,
    detrendedPoints: normalisedDetrendedPoints,
    smoothedPoints: normalisedSmoothedPoints,
    maxSecs:
      typeof lastT === "number" && Number.isFinite(lastT)
        ? Math.max(1, Math.ceil(lastT))
        : 60,
  };
}

export function pickPostExertionDecaySeries(docData) {
  const recovery = pickPostExertionBreathingRecovery(docData);
  const relativeTimes = readArray(recovery?.observedRelativeTimesSeconds);
  const observedValues = readArray(recovery?.observedBreathsPerMinute);
  const expValues = readArray(recovery?.decayFit?.exp?.yhat);
  const linearValues = readArray(recovery?.decayFit?.linear?.yhat);

  const pointCount = Math.max(
    relativeTimes.length,
    observedValues.length,
    expValues.length,
    linearValues.length,
  );

  const points = [];

  for (let index = 0; index < pointCount; index += 1) {
    const time = readFiniteNumber(relativeTimes[index]);
    if (time == null) continue;

    points.push({
      t: time,
      observed: readFiniteNumber(observedValues[index]),
      exp: readFiniteNumber(expValues[index]),
      linear: readFiniteNumber(linearValues[index]),
    });
  }

  return {
    points,
    expR2: readFiniteNumber(recovery?.decayFit?.exp?.r2),
    linearR2: readFiniteNumber(recovery?.decayFit?.linear?.r2),
  };
}
